/**
 * MosaicRosterImportModal.js — v6.32.163
 *
 * CSV roster ingest for multilingual learners. Two-step flow:
 *   1) Paste CSV → click Preview → review parsed rows + errors.
 *   2) Click Commit → creates student users + pre-fills mosaic_intake
 *      stubs + (optional) adds to the selected class.
 *
 * Required CSV columns: name, grade
 * Optional: email, home_language, newcomer, sife, iep_504, former_ell, years_in_us
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Upload, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE = `name,grade,home_language,newcomer,sife,iep_504,former_ell,years_in_us
Maria Ortiz,3,Spanish,true,false,false,false,0.5
Ahmed Khan,4,Arabic,false,true,false,false,3
Lin Zhao,3,Mandarin,true,false,false,false,0
`;

const MosaicRosterImportModal = ({ open, onOpenChange, classId, onComplete }) => {
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committed, setCommitted] = useState(null);

  const reset = () => { setCsvText(''); setPreview(null); setCommitted(null); };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setCsvText(String(r.result || ''));
    r.readAsText(f);
  };

  const callApi = async (commit) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/mosaic/roster/csv`, {
        csv_text: csvText,
        class_id: classId || undefined,
        commit,
      });
      if (!res.data?.ok) {
        toast.error(res.data?.error || 'CSV parse failed');
        return;
      }
      if (commit) {
        setCommitted(res.data);
        toast.success(`Imported ${res.data.students_created} students`);
        if (onComplete) onComplete(res.data);
      } else {
        setPreview(res.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.error || 'CSV import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent data-testid="mosaic-roster-modal" className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Upload className="w-5 h-5 text-violet-600" />
            MOSAIC Roster Import (CSV)
          </DialogTitle>
          <DialogDescription>
            Required columns: <code className="text-xs bg-gray-100 px-1">name, grade</code>. Optional:{' '}
            <code className="text-xs bg-gray-100 px-1">email, home_language, newcomer, sife, iep_504, former_ell, years_in_us</code>.
            Boolean cells accept true/false/yes/no/1/0.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" data-testid="roster-file-input" />
              <span className="px-3 py-1.5 border border-gray-300 hover:bg-gray-50 text-xs font-semibold uppercase tracking-wide">
                Upload .csv
              </span>
            </label>
            <button
              type="button"
              onClick={() => setCsvText(SAMPLE)}
              className="text-xs text-violet-700 hover:underline"
              data-testid="roster-sample-btn"
            >
              Paste sample data
            </button>
          </div>

          <Textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder="name,grade,home_language,newcomer&#10;Maria Ortiz,3,Spanish,true"
            rows={8}
            className="font-mono text-xs"
            data-testid="roster-csv-text"
          />

          {preview && !committed && (
            <div className="border border-gray-300 p-3 text-sm" data-testid="roster-preview">
              <div className="flex items-center gap-3 mb-2">
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="w-4 h-4" /> {preview.valid_rows} valid
                </span>
                {preview.invalid_rows > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-700">
                    <AlertTriangle className="w-4 h-4" /> {preview.invalid_rows} invalid
                  </span>
                )}
              </div>
              <div className="max-h-48 overflow-auto border-t border-gray-200 pt-2">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="py-1">#</th><th>Name</th><th>Grade</th><th>Lang</th><th>Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.preview || []).map((r) => (
                      <tr key={r.row} className="border-t border-gray-100">
                        <td className="py-1 text-gray-400">{r.row}</td>
                        <td className="font-semibold">{r.name}</td>
                        <td>{r.grade}</td>
                        <td>{r.home_language || '—'}</td>
                        <td className="text-gray-600">
                          {r.newcomer ? 'NEW ' : ''}{r.sife ? 'SIFE ' : ''}{r.iep_504 ? 'IEP ' : ''}{r.former_ell ? 'FELL' : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {committed && (
            <div className="border-2 border-emerald-300 bg-emerald-50 p-3 text-sm" data-testid="roster-committed">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                <CheckCircle2 className="w-4 h-4" />
                Imported {committed.students_created} students · {committed.intake_stubs} intake stubs
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="roster-cancel">Close</Button>
            <Button
              variant="outline"
              onClick={() => callApi(false)}
              disabled={busy || !csvText.trim()}
              data-testid="roster-preview-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Preview
            </Button>
            <Button
              onClick={() => callApi(true)}
              disabled={busy || !preview || preview.valid_rows === 0}
              data-testid="roster-commit-btn"
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Commit Import
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MosaicRosterImportModal;
